import React, { useState, useEffect, useRef, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";
import { motion, AnimatePresence } from "motion/react";
import { Avatar } from "../Avatar/Avatar";
import { Badge } from "../Badge/Badge";
import { useAuth } from "../../context/AuthContext";
import { changeUserLanguage } from "../../utilities/translate";
import { MenuState } from "../../store/types/types";
import IDrawerMenus from "../../interfaces/IDrawerMenus";
import IDrawerSubMenus from "../../interfaces/IDrawerSubMenus";
import * as menuAction from "../../store/actionCreators";
import NexLogo from "../../assets/svg/NexLogo";
import styles from "./AppShell.module.css";
import Breadcrumb, { BreadcrumbItem } from "../Breadcrumb/Breadcrumb";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { resolveVisibleMenus } from "../../utilities/localizaMenu";

const COMPACT_KEY = "appshell-compact-state";

export type EnvironmentType = "DEV" | "QA" | "PROD";

export interface AppShellProps {
  children: ReactNode;
  /** Tipo de ambiente exibido na TopBar */
  environment?: EnvironmentType;
  /** Quantidade de notificações não lidas */
  notificationsCount?: number;
  /** Callback ao clicar nas notificações */
  onNotificationClick?: () => void;
}

// Mapeia ícones legados pi-* para Material Symbols
const ICON_MAP: Record<string, string> = {
  "pi-home": "home",
  "pi-users": "group",
  "pi-file": "description",
  "pi-table": "table_chart",
  "pi-database": "database",
  "pi-expand": "open_in_full",
  "pi-box": "inventory_2",
  "pi-sitemap": "account_tree",
  "pi-building": "location_city",
  "pi-th-large": "grid_view",
  "pi-map": "map",
  "pi-monitoring": "monitoring",
  "pi-gauge": "speed",
  "pi-chart-bar": "bar_chart",
  "pi-clock": "schedule",
  "pi-stopwatch": "timer",
  "pi-chart-line": "show_chart",
  "pi-dashboard": "dashboard",
  "pi-history": "timeline",
  "pi-car": "directions_car",
  "pi-bars": "view_list",
  "pi-ellipsis-h": "more_horiz",
  "pi-cog": "settings",
  "pi pi-check-circle": "verified",
  "pi pi-list": "format_list_bulleted",
  "pi pi-sign-out": "logout",
};

const resolveIcon = (icon: string): string =>
  ICON_MAP[icon] ?? ICON_MAP[icon.replace("pi pi-", "pi-")] ?? "circle";

interface NavItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  items?: NavItem[];
}

const mapMenus = (menus: IDrawerMenus[], t: any): NavItem[] =>
  menus.map((m) => ({
    id: m.id,
    label: t(m.resourcekey, m.label),
    icon: resolveIcon(m.icon),
    route: m.route,
    items: m.SubModules ? mapSubMenus(m.SubModules, t) : undefined,
  }));

const mapSubMenus = (subs: IDrawerSubMenus[], t: any): NavItem[] =>
  subs.map((s) => ({
    id: s.id,
    label: t(s.resourceKey, s.label),
    icon: resolveIcon(s.icon),
    route: s.route,
    items: s.SubModules ? mapSubMenus(s.SubModules, t) : undefined,
  }));


const renderIcon = (name: string, filled = false): React.ReactNode => (
  <span
    className="material-symbols-outlined"
    aria-hidden="true"
    style={{ fontSize: "inherit", fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0" }}
  >
    {name}
  </span>
);

const isItemActive = (item: NavItem, route: string): boolean => {
  if (item.route && item.route.toLowerCase() === route.toLowerCase()) return true;
  if (item.items) return item.items.some((c) => isItemActive(c, route));
  return false;
};

// Prefixos que existem apenas como agrupadores de breadcrumb: eles prefixam
// telas reais (/reports/*, /monitoring/*) mas não têm tela própria, então seu
// crumb não deve ser clicável. Qualquer outro segmento sem NavItem correspondente
// é tratado como uma tela real fora do menu visível (ex.: /families, /products,
// /fmeas) e permanece clicável para voltar à listagem.
const SCREENLESS_GROUP_PATHS = new Set(["/reports", "/monitoring"]);

/**
 * Resolve breadcrumb items from the navigation hierarchy and current pathname
 */
const resolveBreadcrumbs = (
  pathname: string,
  navItems: NavItem[],
  t: any
): BreadcrumbItem[] => {
  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentPath = "";
  let currentLevelItems = navItems;

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Try to find a matching NavItem for this segment or the full path so far
    let match = currentLevelItems.find(
      (item) => 
        item.route?.toLowerCase() === currentPath.toLowerCase() ||
        item.route?.split("/").pop()?.toLowerCase() === segment.toLowerCase()
    );

    if (match) {
      breadcrumbs.push({
        label: match.label,
        url: match.route,
        disabled: !match.route, // grupo/segmento sem rota própria → não-clicável
      });
      // Move deeper into the hierarchy if children exist
      if (match.items) {
        currentLevelItems = match.items;
      }
    } else {
      // General segment resolution (agnostic & decoupled)
      let label = segment;
      
      // 1. Detect IDs (UUIDs or numeric)
      const looksLikeId = segment.length > 20 || !isNaN(Number(segment));
      
      if (looksLikeId) {
        // Only show "Details" (or similar) if it's not redundant (e.g. followed by edit/details)
        const nextSegment = segments[index + 1];
        if (!nextSegment || !["edit", "details"].includes(nextSegment.toLowerCase())) {
          label = t("breadcrumb:details", t("form:details", t("menu:details", "Detalhes")));
        } else {
          return; // Skip this segment if it's just an ID followed by an action
        }
      } else {
        // 2. Prioritized Translation Lookup
        const toCamel = (str: string): string => str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        const camelSegment = toCamel(segment);
        const capitalizedFallback = segment.charAt(0).toUpperCase() + segment.slice(1);

        // List of translation keys to try in order
        const translationKeys = [
          `breadcrumb:${segment}`,
          `breadcrumb:${camelSegment}`,
          `menu:${segment}`,
          `menu:${camelSegment}`,
          `form:${segment}`,
          `form:${camelSegment}`,
          `general:${segment}`,
          `general:${camelSegment}`
        ];

        let translated = "";
        for (const key of translationKeys) {
          const result = t(key);
          if (result && result !== key) {
            translated = result;
            break;
          }
        }

        label = translated || capitalizedFallback;
      }

      // Segmento sem NavItem correspondente. Só desabilita os prefixos de grupo
      // sem tela própria (/reports, /monitoring); telas reais fora do menu
      // visível (ex.: /families) continuam clicáveis para voltar à listagem.
      const isScreenlessGroup = SCREENLESS_GROUP_PATHS.has(currentPath.toLowerCase());
      breadcrumbs.push({
        label,
        url: isScreenlessGroup ? undefined : currentPath,
        disabled: isScreenlessGroup,
      });
    }
  });

  return breadcrumbs;
};



export const AppShell: React.FC<AppShellProps> = ({
  children,
  notificationsCount,
  onNotificationClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { userName, userEmail, signOut } = useAuth();

  const menus: readonly IDrawerMenus[] = useSelector(
    (state: MenuState) => state.menus,
    shallowEqual
  );

  const [isCompact, setIsCompact] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(COMPACT_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [userMenuVisible, setUserMenuVisible] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Responsividade: abaixo de 1024px o menu vira drawer sobreposto (off-canvas).
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // No desktop o menu pode ficar compacto (72px); dentro do drawer mobile é sempre expandido.
  const showCompact = !isMobile && isCompact;

  const { t, i18n } = useTranslation(["menu", "form", "general", "breadcrumb"]);
  const currentLang = i18n.language?.toLowerCase().startsWith("en") ? "en" : "pt";

  // Recorte do menu por usuário (gambiarra temporária do POC Localiza — ver
  // utilities/localizaMenu.ts). Precisa acontecer aqui, no render, porque o
  // estado inicial do Redux é montado no import, antes de haver login.
  const visibleMenus = useMemo(
    () => resolveVisibleMenus(menus, userEmail),
    [menus, userEmail],
  );
  const navItems = mapMenus(visibleMenus, t);

  const activeRoute = location.pathname;

  const breadcrumbItems = useMemo(
    () => resolveBreadcrumbs(activeRoute, navItems, t),
    [activeRoute, navItems, t]
  );

  // Persist compact state
  useEffect(() => {
    try {
      localStorage.setItem(COMPACT_KEY, JSON.stringify(isCompact));
    } catch {}
  }, [isCompact]);

  // Fecha o drawer ao navegar (mobile) — todo navigate() muda o pathname.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Trava o scroll do body enquanto o drawer está aberto (mobile).
  useEffect(() => {
    if (!(isMobile && drawerOpen)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, drawerOpen]);

  // Auto-expand parent of active route
  useEffect(() => {
    setExpandedMap((prev) => {
      const newMap = { ...prev };
      let changed = false;
      
      const expandActive = (items: NavItem[]): void => {
        for (const item of items) {
          if (isItemActive(item, activeRoute)) {
            if (!newMap[item.id]) {
              newMap[item.id] = true;
              changed = true;
            }
          }
          if (item.items) {
            expandActive(item.items);
          }
        }
      };
      
      expandActive(navItems);
      return changed ? newMap : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute]);

  // No mobile o botão abre/fecha o drawer; no desktop alterna compacto/expandido.
  const handleToggle = (): void => {
    if (isMobile) {
      setDrawerOpen((prev) => !prev);
      return;
    }
    setIsCompact((prev) => !prev);
    dispatch(menuAction.changeDrawer());
  };

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleItemClick = (_: React.MouseEvent<HTMLButtonElement>, item: NavItem): void => {
    if (item.items && item.items.length > 0) {
      if (showCompact) {
        setIsCompact(false);
        dispatch(menuAction.changeDrawer());
        setExpandedMap((prev) => ({ ...prev, [item.id]: true }));
        setHoveredId(null);
      } else {
        setExpandedMap((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
      }
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const sidebarWidth = showCompact ? 72 : 280;
  // No mobile o conteúdo ocupa 100% (o drawer é sobreposto, não reserva espaço).
  const contentMarginLeft = isMobile ? 0 : sidebarWidth;

  return (
    <div className={styles.shell}>
      {/* =========================================================
          SIDEBAR
          ========================================================= */}
      <aside
        className={`${styles.sidebar} ${showCompact ? styles.compact : ""} ${
          isMobile ? styles.drawer : ""
        } ${isMobile && drawerOpen ? styles.drawerOpen : ""}`}
      >
        {/* Logo */}
        <div className={styles.sidebarHeader}>
          <NexLogo size={32} className={styles.logoIcon} style={{ color: "var(--color-secondary-300)" }} />
          {!showCompact && <span className={styles.logoText}>Nex</span>}
        </div>

        {/* Navigation */}
        <nav className={styles.navigation}>
          {showCompact && <Tooltip target=".shell-tooltip" position="right" showDelay={300} />}
          <ul className={styles.menuList}>
            {navItems.map((item) => {
              const active = isItemActive(item, activeRoute);
              const expanded = expandedMap[item.id] && !showCompact;
              const hasChildren = item.items && item.items.length > 0;

              return (
                <li
                  key={item.id}
                  className={styles.menuItemContainer}
                  onMouseEnter={() => showCompact && setHoveredId(item.id)}
                  onMouseLeave={() => showCompact && setHoveredId(null)}
                >
                  <button
                    type="button"
                    className={`${styles.menuItem} ${active ? styles.active : ""} shell-tooltip`}
                    onClick={(e) => handleItemClick(e, item)}
                    data-pr-tooltip={showCompact ? item.label : ""}
                  >
                    <span className={styles.menuItemIcon}>
                      {renderIcon(item.icon, active)}
                    </span>
                    {!showCompact && (
                      <span className={styles.menuItemLabel}>{item.label}</span>
                    )}
                    {!showCompact && hasChildren && (
                      <span className={styles.menuItemChevron}>
                        {renderIcon(expanded ? "keyboard_arrow_up" : "keyboard_arrow_down")}
                      </span>
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {expanded && hasChildren && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <ul className={styles.subMenuList}>
                          {item.items!.map((child) => {
                            const childActive = isItemActive(child, activeRoute);
                            const childExpanded = expandedMap[child.id] && !showCompact;
                            const childHasChildren = child.items && child.items.length > 0;

                            return (
                              <li key={child.id}>
                                <button
                                  type="button"
                                  className={`${styles.subMenuItem} ${childActive ? styles.active : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (childHasChildren) {
                                      setExpandedMap((prev) => ({ ...prev, [child.id]: !prev[child.id] }));
                                    } else if (child.route) {
                                      navigate(child.route);
                                    }
                                  }}
                                >
                                  <span className={styles.menuItemIcon}>
                                    {renderIcon(child.icon, childActive)}
                                  </span>
                                  <span className={styles.menuItemLabel}>{child.label}</span>
                                  {childHasChildren && (
                                    <span className={styles.menuItemChevron}>
                                      {renderIcon(childExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down")}
                                    </span>
                                  )}
                                </button>
                                {/* Terceiro nível */}
                                <AnimatePresence initial={false}>
                                  {childExpanded && childHasChildren && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.15, ease: "easeInOut" }}
                                      style={{ overflow: "hidden" }}
                                    >
                                      <ul className={styles.subMenuList} style={{ paddingLeft: "1.5rem" }}>
                                        {child.items!.map((grandchild) => {
                                          const gcActive = isItemActive(grandchild, activeRoute);
                                          return (
                                            <li key={grandchild.id}>
                                              <button
                                                type="button"
                                                className={`${styles.subMenuItem} ${gcActive ? styles.active : ""}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (grandchild.route) navigate(grandchild.route);
                                                }}
                                              >
                                                <span className={styles.menuItemIcon}>
                                                  {renderIcon(grandchild.icon, gcActive)}
                                                </span>
                                                <span className={styles.menuItemLabel}>{grandchild.label}</span>
                                              </button>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Flyout em Compact Mode */}
                  <AnimatePresence>
                    {showCompact && hasChildren && hoveredId === item.id && (
                      <motion.div
                        className={styles.flyoutMenuWrapper}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.15 }}
                      >
                        <div className={styles.flyoutMenuHeader}>{item.label}</div>
                        <ul className={styles.flyoutMenuList}>
                          {item.items!.map((child) => {
                            const childActive = isItemActive(child, activeRoute);
                            const childHasChildren = child.items && child.items.length > 0;

                            return (
                              <li key={child.id}>
                                <button
                                  type="button"
                                  className={`${styles.subMenuItem} ${childActive ? styles.active : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (child.route) {
                                      navigate(child.route);
                                      setHoveredId(null);
                                    }
                                  }}
                                >
                                  <span className={styles.menuItemIcon}>
                                    {renderIcon(child.icon, childActive)}
                                  </span>
                                  <span className={styles.menuItemLabel}>{child.label}</span>
                                </button>
                                {childHasChildren && (
                                  <ul className={styles.flyoutMenuList} style={{ paddingLeft: "1.5rem" }}>
                                    {child.items!.map((grandchild) => {
                                      const gcActive = isItemActive(grandchild, activeRoute);
                                      return (
                                        <li key={grandchild.id}>
                                          <button
                                            type="button"
                                            className={`${styles.subMenuItem} ${gcActive ? styles.active : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (grandchild.route) {
                                                navigate(grandchild.route);
                                                setHoveredId(null);
                                              }
                                            }}
                                          >
                                            <span className={styles.menuItemIcon}>
                                              {renderIcon(grandchild.icon, gcActive)}
                                            </span>
                                            <span className={styles.menuItemLabel}>{grandchild.label}</span>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

      </aside>

      {/* Backdrop do drawer (mobile): clique fecha o menu. */}
      {isMobile && drawerOpen && (
        <div className={styles.backdrop} onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      {/* =========================================================
          MAIN AREA (TopBar + Content)
          ========================================================= */}
      <div
        className={styles.mainArea}
        style={{ marginLeft: contentMarginLeft }}
      >
        {/* TopBar */}
        <header className={styles.topBar}>
          <div className={styles.topBarStart}>
            <button
              type="button"
              className={styles.toggleBtn}
              onClick={handleToggle}
              aria-label="Toggle Menu"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            {!isMobile && (
              <Breadcrumb
                home={{ icon: "home", url: "/" }}
                items={breadcrumbItems}
                className={styles.breadcrumb}
              />
            )}
          </div>

          <div className={styles.topBarEnd}>
            {notificationsCount !== undefined && (
              <div className={styles.notifWrapper}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={onNotificationClick}
                  aria-label="Notificações"
                >
                  <span className="material-symbols-outlined">notifications</span>
                </button>
                <Badge value={notificationsCount} className={styles.notifBadge} />
              </div>
            )}

            {/* Idioma: no mobile vira só um ícone de globo clicável (alterna PT/EN);
                no desktop, o Dropdown do design system. */}
            {isMobile ? (
              <button
                type="button"
                className={styles.langMobileBtn}
                onClick={() => void changeUserLanguage(currentLang === "en" ? "pt" : "en")}
                aria-label={t("general:language")}
                title={currentLang === "en" ? "English" : "Português (BR)"}
              >
                <span className="material-symbols-outlined">language</span>
                <span className={styles.langMobileCode}>{currentLang.toUpperCase()}</span>
              </button>
            ) : (
              <div className={styles.langSelect}>
                <Dropdown
                  value={currentLang}
                  options={[
                    { label: "Português (BR)", value: "pt" },
                    { label: "English", value: "en" },
                  ]}
                  onChange={(e) => void changeUserLanguage(e.value)}
                  valueTemplate={() => (
                    <span className={styles.langValue}>
                      <span className="material-symbols-outlined" aria-hidden="true">language</span>
                      <span className={styles.langLabel}>
                        {currentLang === "en" ? "English" : "Português (BR)"}
                      </span>
                    </span>
                  )}
                  className={styles.langDropdown}
                  aria-label={t("general:language")}
                />
              </div>
            )}

            {/* User Profile */}
            <div className={styles.userArea} ref={userMenuRef}>
              <button
                type="button"
                className={styles.userProfile}
                onClick={() => setUserMenuVisible((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={userMenuVisible}
                aria-label="Menu do usuário"
              >
                <Avatar type="person" name={userName || userEmail} className={styles.userAvatar} />
                <div className={styles.userInfo}>
                  {userName && <span className={styles.userName}>{userName}</span>}
                  <span className={styles.userEmail}>{userEmail}</span>
                </div>
                <span className={`material-symbols-outlined ${styles.chevron}`}>expand_more</span>
              </button>

              <AnimatePresence>
                {userMenuVisible && (
                  <motion.div
                    className={styles.userMenu}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      onClick={() => { setUserMenuVisible(false); signOut(); }}
                    >
                      <span className="material-symbols-outlined">logout</span>
                      {t("general:signout")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={styles.content}>
          {children}
        </main>
      </div>
    </div>
  );
};

AppShell.displayName = "AppShell";
export default AppShell;
